import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * queryKeys — tenant-safe, hierarchical TanStack Query key factory.
 *
 * ## Why this exists (A5-023)
 *
 * The original convention keyed every API query as `[endpoint, tenantId]`,
 * e.g. `['/api/v1/equipment', 'tenant-a']`. TanStack Query matches
 * invalidation by **prefix**: `invalidateQueries({ queryKey: ['/api/v1/equipment'] })`
 * matches ANY key whose first element is `'/api/v1/equipment'` — including
 * `['/api/v1/equipment', 'tenant-b']`. Because `tenantId` sat AFTER the
 * resource, a blind resource-level invalidation crossed tenant boundaries: a
 * write in tenant A could evict and refetch tenant B's cached data.
 *
 * The fix is purely structural. We hoist `tenantId` to a fixed, early position
 * in the key so that the tenant becomes part of every meaningful prefix:
 *
 *     ['api', 'v1', tenantId, ...resourceSegments, ...filters]
 *
 * Now `['api', 'v1', 'tenant-a', 'equipment']` and
 * `['api', 'v1', 'tenant-b', 'equipment']` share NO prefix beyond
 * `['api','v1']`, so a tenant-scoped invalidation can never leak across the
 * tenant boundary. A cross-module invalidation (`equipment` vs `production`)
 * is likewise isolated because the module/resource segment diverges within the
 * same tenant subtree.
 *
 * ## Back-compat
 *
 * `useApiQuery` / `useApiInfiniteQuery` route through {@link apiQueryKey}, so
 * existing hook callers keep their call signatures unchanged — only the
 * underlying key shape moves. Call sites that previously invalidated by raw
 * endpoint (`['/api/v1/equipment']`) should migrate to
 * {@link invalidateApiQueries} / {@link tenantRoot}, which are tenant-scoped by
 * construction. A legacy invalidation that omits the tenant now simply matches
 * nothing (fails safe) rather than matching every tenant.
 */

/** Root namespace every Tyndo API query key starts with. */
export const API_NAMESPACE = ['api', 'v1'] as const;

/**
 * Sentinel used when a query is not tenant-scoped (e.g. a pre-auth bootstrap
 * call made before a tenant is resolved). Kept distinct from any real tenant id
 * so a global query can never collide with — nor be invalidated by — a
 * tenant-scoped one.
 */
export const GLOBAL_TENANT = '__global__';

/**
 * Normalize a raw endpoint string into ordered path segments + a stable,
 * sorted filter object split out of the query string.
 *
 * `/api/v1/equipment/abc?status=Running&line=L1`
 *   -> { segments: ['equipment','abc'], filters: { line:'L1', status:'Running' } }
 *
 * - The leading `/api/vN` prefix is stripped (it is re-added as the namespace)
 *   so an endpoint written with or without the prefix maps to the same key.
 * - Query params are sorted by key so two callers passing the same filters in a
 *   different textual order share one cache entry.
 */
export function parseEndpoint(endpoint: string): {
  segments: string[];
  filters: Record<string, string> | null;
} {
  const queryIndex = endpoint.indexOf('?');
  const rawPath = queryIndex === -1 ? endpoint : endpoint.slice(0, queryIndex);
  const rawQuery = queryIndex === -1 ? '' : endpoint.slice(queryIndex + 1);
  const path = rawPath.replace(/^\/+/, '').replace(/^api\/v\d+\/?/, '');
  const segments = path.split('/').filter((s) => s.length > 0);

  if (!rawQuery) {
    return { segments, filters: null };
  }

  const params = new URLSearchParams(rawQuery);
  const filters: Record<string, string> = {};
  // Sort keys so filter ordering in the URL is irrelevant to the cache key.
  for (const key of [...params.keys()].sort()) {
    filters[key] = params.get(key) ?? '';
  }
  return { segments, filters };
}

/**
 * Build the tenant-safe hierarchical key for an API endpoint.
 *
 *     apiQueryKey('/api/v1/equipment?status=Running', 'tenant-a')
 *       -> ['api','v1','tenant-a','equipment',{ status:'Running' }]
 *
 * The shape is `['api','v1', tenantId, ...segments, filters?]`. `tenantId` is
 * the FIRST variable segment, so every resource/module prefix beneath it is
 * inherently tenant-scoped.
 */
export function apiQueryKey(
  endpoint: string,
  tenantId: string | null | undefined,
): QueryKey {
  const { segments, filters } = parseEndpoint(endpoint);
  const key: unknown[] = [...API_NAMESPACE, tenantId ?? GLOBAL_TENANT, ...segments];
  if (filters) key.push(filters);
  return key as QueryKey;
}

/**
 * The tenant root prefix — `['api','v1', tenantId]`. Invalidating with this key
 * clears EVERY query for one tenant and nothing belonging to another.
 */
export function tenantRoot(tenantId: string | null | undefined): QueryKey {
  return [...API_NAMESPACE, tenantId ?? GLOBAL_TENANT] as QueryKey;
}

/**
 * A tenant-scoped resource prefix used for invalidation. Pass the same endpoint
 * (or a resource prefix of it) you'd query with; the query-string is ignored so
 * the prefix matches all filtered variants of that resource within the tenant.
 *
 *     resourceKey('/api/v1/equipment', 'tenant-a')
 *       -> ['api','v1','tenant-a','equipment']
 *
 * This is a strict prefix of `apiQueryKey('/api/v1/equipment?...','tenant-a')`,
 * so TanStack prefix-matching invalidates every cached filter variant for that
 * resource — but only inside `tenant-a`.
 */
export function resourceKey(
  endpoint: string,
  tenantId: string | null | undefined,
): QueryKey {
  const { segments } = parseEndpoint(endpoint);
  return [...API_NAMESPACE, tenantId ?? GLOBAL_TENANT, ...segments] as QueryKey;
}

/**
 * Tenant-scoped invalidation helper. Prefer this over a bare
 * `queryClient.invalidateQueries({ queryKey: [endpoint] })`, which under the new
 * scheme would match nothing (fails safe) and under the OLD scheme could match
 * across tenants.
 *
 *     invalidateApiQueries(queryClient, 'tenant-a', '/equipment');
 *     invalidateApiQueries(queryClient, 'tenant-a', ['/equipment', '/production']);
 *
 * Pass no resource (or an empty list) to invalidate the entire tenant subtree.
 */
export function invalidateApiQueries(
  queryClient: QueryClient,
  tenantId: string | null | undefined,
  resource?: string | string[],
): Promise<void> {
  const resources =
    resource === undefined
      ? null
      : Array.isArray(resource)
        ? resource
        : [resource];

  if (!resources || resources.length === 0) {
    return queryClient.invalidateQueries({ queryKey: tenantRoot(tenantId) });
  }

  return Promise.all(
    resources.map((r) =>
      queryClient.invalidateQueries({ queryKey: resourceKey(r, tenantId) }),
    ),
  ).then(() => undefined);
}

/**
 * Named, tenant-scoped resource-key builders.
 *
 * `resourceKey('/equipment/' + id, tenantId)` works but re-encodes the endpoint
 * path as a string at every call site, so a path refactor (`/equipment/{id}` ->
 * `/assets/{id}`) silently desynchronises a read from its invalidation. These
 * builders are the single source of truth for the resource path of each
 * realtime-bearing aggregate: a read keys with `QueryKeys.equipment(t, id)` and
 * a live event invalidates with the SAME builder, so the two can never drift.
 *
 * Each builder returns the strict tenant-scoped prefix
 * `['api','v1', tenantId, ...segments]`, identical in shape to {@link resourceKey}
 * and a prefix of the filtered `apiQueryKey` variants — so TanStack prefix
 * matching invalidates every cached filter variant of that resource, but only
 * within the given tenant (A5-023, A5-551).
 *
 * Pass `undefined`/`null` for an id to key the collection root (e.g.
 * `QueryKeys.equipment(t)` -> `[...,'equipment']`, the parent of every item).
 */
export const QueryKeys = {
  equipment: (tenantId: string | null | undefined, equipmentId?: string) =>
    resourceKey(
      equipmentId ? `/equipment/${equipmentId}` : '/equipment',
      tenantId,
    ),
  production: (tenantId: string | null | undefined, lineId?: string) =>
    resourceKey(lineId ? `/production/${lineId}` : '/production', tenantId),
  job: (tenantId: string | null | undefined, jobId: string) =>
    resourceKey(`/jobs/${jobId}`, tenantId),
  alarms: (tenantId: string | null | undefined) =>
    resourceKey('/alarms', tenantId),
  lineEquipment: (tenantId: string | null | undefined, lineId: string) =>
    resourceKey(`/lines/${lineId}/equipment`, tenantId),
  lineKpis: (tenantId: string | null | undefined, lineId: string) =>
    resourceKey(`/analytics/kpis/${lineId}`, tenantId),
  maintenancePrediction: (
    tenantId: string | null | undefined,
    equipmentId: string,
  ) => resourceKey(`/maintenance/predictions/${equipmentId}`, tenantId),
} as const;

/**
 * Convenience namespace so callers can `import { queryKeys } from '@tyndo/hooks'`
 * and use a single grouped factory rather than several free functions.
 */
export const queryKeys = {
  namespace: API_NAMESPACE,
  global: GLOBAL_TENANT,
  api: apiQueryKey,
  tenantRoot,
  resource: resourceKey,
  parse: parseEndpoint,
  /** Named resource builders, e.g. `queryKeys.keys.equipment(tenantId, id)`. */
  keys: QueryKeys,
} as const;
